import { type SchoolExtended } from './schoolInterface';

export interface User {
	token: string;
	id: number;
	email: string;
	username: string;
	firstname: string;
	lastname: string;
	role: UserRole;
	schools: SchoolExtended[];
	// Keys of school ids (or 0 for self) and then array of favs for that school
	resourceFavourites: { [key: number]: number[] };
	toggles: UserToggles;
	/**
	 * The IDs of the LMS courses the user has access to.
	 *
	 * This is relevant for non-special users,  eg teachers and champions. The source of truth for their course access
	 * is the `course_access` user meta field on this site, not the LMS site.
	 *
	 * For admins, efs, and other special users, their course access might be changed in the LMS (which would not update here).
	 */
	courseIds: number[];
	/**
	 * School cluster name the the user is allowed to view. They don't need to be a champion necessarily.
	 *
	 * Look at {@link User.schools} {@link SchoolExtended} for the cluster name associated to the school.
	 */
	cluster: string;
}

export interface UserStore {
	currentSchoolId: number;
	loginLoading: boolean;
	isReloading: boolean;
}

/**
 * The valid toggle names. This interface is the only place they're declared — the backend
 * passes the toggle map through from user meta as-is, so adding one here is all that's
 * needed. `updateToggles` takes `keyof UserToggles`, which is what catches typos.
 *
 * Every toggle is optional: a user has no entry for a toggle until they first set it, so
 * absent means "not set" — read them as falsy rather than expecting `false`.
 */
export interface UserToggles {
	hideSafariResourcePrintAdvice?: boolean;
	hideRecommendPopup?: boolean;
	hasSubmittedSuspendSmarterForm?: boolean;
	/** The 21 August 2026 cohort. Kept so those registrations aren't orphaned. */
	hasSubmittedTheRestorativeClassroomPlaybookForm?: boolean;
	hasSubmittedTheRestorativeClassroomPlaybookDec2026Form?: boolean;
	hasSubmittedTheBetterBoysBlueprintForm?: boolean;
	hasSubmittedTheShameResponsiveSchoolForm?: boolean;
	hasSubmittedTheAIReadyStudentForm?: boolean;
	hasSubmittedTheTeacherRetentionSolutionForm?: boolean;
	hasSubmittedTheBullyingMythForm?: boolean;
}

export type UserRole = (typeof userRole)[keyof typeof userRole];
export const userRole = {
	Administrator: 'administrator',
	Editor: 'editor',
	Ef: 'ef',
	Operations: 'operations',
	Champion: 'champion',
	Teacher: 'teacher',
} as const;

export type UserJobTitle = (typeof userJobTitle)[keyof typeof userJobTitle];
export const userJobTitle = {
	Principal: 'principal',
	AssistantDeputyPrincipal: 'assistant-principal',
	HeadOfCollege: 'head-of-college',
	SeniorLeader: 'senior-leader',
	WellbeingCoordinator: 'wellbeing-coordinator',
	HeadOfTeachingAndLearning: 'head-of-teaching',
	Teacher: 'teacher',
	EducationSupportStaff: 'Education Support Staff',
	CareerAdvisor: 'career-advisor',
	Administration: 'admin',
	Accounts: 'accounts',
	BusinessManager: 'business-manager',
	Other: 'other',
} as const;

/**
 * What to show a person for each job title.
 *
 * The values above are the options of HubSpot's `job_title_new` enumeration and
 * several of them don't read as anything ('admin', 'head-of-teaching'), so
 * anywhere one is shown needs this. The wording is HubSpot's own option labels,
 * so a title picked here and a title picked in HubSpot are the same words.
 *
 * Typed as a full Record, which is what makes it a compile error to add an
 * option above and forget to name it here.
 */
export const userJobTitleLabels: Record<UserJobTitle, string> = {
	principal: 'Principal',
	'assistant-principal': 'Assistant/Deputy Principal',
	'head-of-college': 'Head of College',
	'senior-leader': 'Senior Leader',
	'wellbeing-coordinator': 'Wellbeing Coordinator',
	'head-of-teaching': 'Head of Teaching and Learning',
	teacher: 'Teacher',
	'Education Support Staff': 'Education Support Staff',
	'career-advisor': 'Career Advisor',
	admin: 'Administration',
	accounts: 'Accounts',
	'business-manager': 'Business Manager',
	other: 'Other',
};

/** The job titles as a picker wants them, in the order HubSpot lists them. */
export const userJobTitleOptions: { value: UserJobTitle; label: string }[] = Object.values(userJobTitle).map((value) => ({
	value,
	label: userJobTitleLabels[value],
}));
